import { useMemo, useState } from 'react';
import { CheckCheck, Download, RotateCcw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FinanceBulkImportRowStatus } from '@/graphql/generatedTypes';
import { EditingCell, ImportTable } from './ImportTable';
import {
  ImportFile,
  ImportResult,
  countByStatus,
  resultsByRowNumber,
} from './importModel';

export type ReviewFilter = 'errors' | 'changes' | 'unchanged' | 'all';

const FILTER_STATUS: Record<ReviewFilter, FinanceBulkImportRowStatus | null> = {
  errors: FinanceBulkImportRowStatus.Error,
  changes: FinanceBulkImportRowStatus.Changed,
  unchanged: FinanceBulkImportRowStatus.Unchanged,
  all: null,
};

type ReviewPanelProps = {
  file: ImportFile;
  validation: ImportResult;
  validatedFile: ImportFile;
  countries: string[];
  validating: boolean;
  /** An edit is committed but not validated yet, or the validation failed. */
  dirty: boolean;
  applying: boolean;
  filter: ReviewFilter;
  onFilterChange: (filter: ReviewFilter) => void;
  editing: EditingCell;
  onEditingChange: (cell: EditingCell) => void;
  onCommit: (rowNumber: number, column: string, value: string) => void;
  onRemove: (rowNumber: number) => void;
  onDownloadErrors: () => void;
  onApply: () => void;
  onReset: () => void;
};

export function ReviewPanel(props: ReviewPanelProps) {
  const { file, validation, validatedFile, filter, validating, dirty } = props;
  const [confirming, setConfirming] = useState(false);
  const results = useMemo(() => resultsByRowNumber(validation), [validation]);
  const counts = countByStatus(validation.rows);
  const errorCount = counts[FinanceBulkImportRowStatus.Error];
  const changeCount = counts[FinanceBulkImportRowStatus.Changed];

  const status = FILTER_STATUS[filter];
  // A row the server has not seen yet (none today) only shows under All.
  const rows = file.rows.filter(
    (row) => status === null || results.get(row.rowNumber)?.status === status,
  );

  const canApply =
    !validating &&
    !dirty &&
    props.editing === null &&
    errorCount === 0 &&
    changeCount > 0;
  let applyHint = '';
  if (errorCount > 0) {
    applyHint = 'Fix or remove the rows with errors first.';
  } else if (dirty || validating || props.editing) {
    applyHint = 'Waiting for the validation of your edits.';
  } else if (changeCount === 0) {
    applyHint = 'The file changes nothing.';
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-4 tw:text-sm">
          <span className="tw:font-medium">{file.fileName}</span>
          <span data-testid="count-changes">
            <strong>{changeCount}</strong>{' '}
            {changeCount === 1 ? 'change' : 'changes'}
          </span>
          <span data-testid="count-unchanged">
            <strong>{counts[FinanceBulkImportRowStatus.Unchanged]}</strong>{' '}
            unchanged
          </span>
          <span
            data-testid="count-errors"
            className={errorCount > 0 ? 'tw:text-destructive' : undefined}
          >
            <strong>{errorCount}</strong>{' '}
            {errorCount === 1 ? 'error' : 'errors'}
          </span>
          {validating && (
            <span className="tw:text-muted-foreground tw:inline-flex tw:items-center tw:gap-1">
              <Spinner /> Validating…
            </span>
          )}
        </div>
        <div className="tw:flex tw:flex-wrap tw:gap-2">
          <Button variant="ghost" onClick={props.onReset}>
            <RotateCcw />
            Start new import
          </Button>
          <Button
            variant="outline"
            disabled={errorCount === 0}
            onClick={props.onDownloadErrors}
          >
            <Download />
            Download rows with errors
          </Button>
          <Button
            disabled={!canApply || props.applying}
            onClick={() => setConfirming(true)}
            title={applyHint || undefined}
          >
            {props.applying ? <Spinner /> : <CheckCheck />}
            Apply {changeCount} {changeCount === 1 ? 'change' : 'changes'}
          </Button>
        </div>
      </div>
      {applyHint && (
        <p className="tw:text-muted-foreground tw:-mt-2 tw:text-right tw:text-xs">
          {applyHint}
        </p>
      )}

      <Tabs
        value={filter}
        onValueChange={(value) => props.onFilterChange(value as ReviewFilter)}
      >
        <TabsList>
          <TabsTrigger value="errors">Errors ({errorCount})</TabsTrigger>
          <TabsTrigger value="changes">Changes ({changeCount})</TabsTrigger>
          <TabsTrigger value="unchanged">
            Unchanged ({counts[FinanceBulkImportRowStatus.Unchanged]})
          </TabsTrigger>
          <TabsTrigger value="all">All ({file.rows.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {rows.length === 0 ? (
        <p className="tw:text-muted-foreground tw:py-6 tw:text-center tw:text-sm">
          No rows in this view.
        </p>
      ) : (
        <ImportTable
          file={file}
          resultFile={validatedFile}
          rows={rows}
          results={results}
          importedColumns={validation.imported_columns}
          countries={props.countries}
          validating={validating}
          editing={props.editing}
          onEditingChange={props.onEditingChange}
          onCommit={props.onCommit}
          onRemove={props.onRemove}
        />
      )}

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Apply {changeCount} {changeCount === 1 ? 'change' : 'changes'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {changeCount} {changeCount === 1 ? 'row' : 'rows'} will be
              written; {counts[FinanceBulkImportRowStatus.Unchanged]} unchanged{' '}
              {counts[FinanceBulkImportRowStatus.Unchanged] === 1
                ? 'row is'
                : 'rows are'}{' '}
              skipped. A row whose stored values changed since this validation
              is not written and comes back as a conflict.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirming(false);
                props.onApply();
              }}
            >
              Apply
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
